import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Checkbox, Table } from 'antd';
import DateRangePicker, {
  filterFactory,
  filter_this_month,
  variant_dropdown,
} from '../../components/filters/DateRangePicker';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import useDateRangeFilter from '../../hooks/utility/useDateRangeFilter';

const sorterDirections = {
  ascend: 'ascend',
  descend: 'descend',
};

const sorterFields = {
  total_orders: 'total_orders',
  total_sales: 'total_sales',
  days_live_count: 'days_live_count',
  early_performance_success: 'early_performance_success',
};

export default function NewRestaurantsTab({ selectedManagers, renderManagerSelect }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:new_restaurants_tab' });

  const { CustomerBaseStatsManager } = useApiClient();

  const { formatCurrency } = useHelpers();

  const [loading, setLoading] = useState(true);
  const [dateRangeFilter, setDateRangeFilter] = useDateRangeFilter(filterFactory(filter_this_month));
  const [data, setData] = useState(null);
  const [sorter, setSorter] = useState({
    field: sorterFields.total_sales,
    order: sorterDirections.descend,
  });
  const [earlyPerformanceFilter, setEarlyPerformanceFilter] = useState(false);

  useEffect(() => {
    fetchData(data?.current_page || 1, sorter);
  }, [selectedManagers, dateRangeFilter, earlyPerformanceFilter]);

  const fetchData = async (page, sorter) => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getPerformanceStats({
      include_days_live_count: true,
      include_early_performance_indicator: true,
      early_performance_filter: earlyPerformanceFilter,
      start_date: dateRangeFilter.start_date,
      end_date: dateRangeFilter.end_date,
      page,
      sort_field: sorter.field,
      sort_order: sorter.order === sorterDirections.ascend ? 'asc' : 'desc',
      managers: selectedManagers,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setData(data);
  };

  const handleTableChange = (pagination, filters, pendingSorter) => {
    const newSorter = {
      field: pendingSorter.field || sorter.field,
      order:
        pendingSorter.order ||
        (sorter.order === sorterDirections.descend ? sorterDirections.ascend : sorterDirections.descend),
    };

    setSorter(newSorter);

    fetchData(pagination.current, newSorter);
  };

  const columns = [
    {
      title: t('table_columns.name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('table_columns.sales_manager'),
      key: 'sales_manager_full_name',
      dataIndex: 'sales_manager_full_name',
    },
    {
      title: t('table_columns.orders'),
      key: 'total_orders',
      dataIndex: 'total_orders',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_orders ? sorter.order : null,
    },
    {
      title: t('table_columns.sales'),
      key: 'total_sales',
      dataIndex: 'total_sales',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_sales ? sorter.order : null,
      render: (value) => formatCurrency(value),
    },
    {
      title: t('table_columns.days_live'),
      key: 'days_live_count',
      dataIndex: 'days_live_count',
      sorter: true,
      sortOrder: sorter.field === sorterFields.days_live_count ? sorter.order : null,
    },
    {
      title: t('table_columns.early_performance'),
      key: 'early_performance_success',
      dataIndex: 'early_performance_success',
      sorter: true,
      sortOrder: sorter.field === sorterFields.early_performance_success ? sorter.order : null,
      render: (value) => (value ? t('labels.early_performance_yes') : t('labels.early_performance_no')),
    },
  ];

  return (
    <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
      <div className="p-md border-b border-brand-inkGrey-grey_2">
        <div className="flex flex-col lg:flex-row items-center gap-4">
          <DateRangePicker
            variant={variant_dropdown}
            defaultFilter={filter_this_month}
            initialValue={dateRangeFilter}
            onChange={setDateRangeFilter}
          />
          <div className="flex flex-row items-center ml-lg">
            <label htmlFor="early-performance-checkbox" className="font-semibold mr-sm mb-none cursor-pointer">
              {t('labels.only_with_early_performance')}
            </label>

            <Checkbox
              id="early-performance-checkbox"
              checked={earlyPerformanceFilter}
              onChange={() => setEarlyPerformanceFilter(!earlyPerformanceFilter)}
            />
          </div>
        </div>
        {renderManagerSelect()}
      </div>
      <Table
        dataSource={data?.data}
        columns={columns}
        pagination={{
          current: data?.current_page,
          total: data?.total,
          pageSize: data?.per_page,
          className: 'pr-[5px]',
        }}
        sorter={sorter}
        loading={loading}
        className="rounded-none shadow-card bg-white overflow-hidden"
        onChange={handleTableChange}
        rowKey={(item) => item.id}
        locale={{
          emptyText: t('labels.no_data'),
          triggerDesc: t('labels.click_to_sort'),
          triggerAsc: t('labels.click_to_sort'),
          cancelSort: t('labels.click_to_sort'),
        }}
      />
    </div>
  );
}
